import { useState, type FormEvent } from 'react';
import { loginRequest } from '@redirector/shared';
import { Logo } from '../components/Logo.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Field } from '../ui/Field.js';

export type LoginScreenProps = { signIn: (email: string, password: string) => Promise<void> };

const INPUT_CLASS =
  'h-(--control-height) rounded-(--radius-md) border border-input bg-card px-(--space-3) text-ink placeholder:text-ink-subtle';

/**
 * The one screen an operator sees before they are anybody.
 *
 * A card on a painted field rather than a form on a bare page. The gradient is
 * built from the palette's own accent and surface tokens — no new colours, so
 * it restyles itself in dark mode — and it is doing one job: making the card
 * the only lit object on the screen, so the eye has exactly one place to go.
 * The mark sits inside the card with the product name, because this is the
 * only place in the app where the product has to introduce itself.
 */
export function LoginScreen({ signIn }: LoginScreenProps) {
  const [fields, setFields] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailure(null);

    // The very schema the server validates with.
    const parsed = loginRequest.safeParse(fields);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'root');
        next[key] ??= issue.message;
      }
      setErrors(next);
      return;
    }

    setErrors({});
    setBusy(true);
    try {
      await signIn(parsed.data.email, parsed.data.password);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-field grid min-h-screen place-items-center px-(--space-4) py-(--space-6)">
      <div className="w-full max-w-[26rem]">
        <div className="flex flex-col gap-(--space-5) rounded-(--radius-lg) border border-rule bg-surface-raised px-(--space-6) py-(--space-6) shadow-(--shadow-overlay)">
          <header className="flex flex-col gap-(--space-3)">
            <Logo size={40} />
            <div>
              <h1 className="text-[length:var(--text-title)] font-bold">Redirector Engine</h1>
              <p className="mt-(--space-1) text-[length:var(--text-small)] font-medium text-ink-muted">
                Sign in to manage domains, URLs and traffic splits.
              </p>
            </div>
          </header>

          {failure !== null && <Callout tone="danger">{failure}</Callout>}

          <form onSubmit={submit} className="flex flex-col gap-(--space-4)" noValidate>
            <Field label="Email" error={errors.email}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  type="email"
                  autoComplete="username"
                  autoFocus
                  className={INPUT_CLASS}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={fields.email}
                  onChange={(event) =>
                    setFields((prev) => ({ ...prev, email: event.target.value }))
                  }
                />
              )}
            </Field>

            <Field label="Password" error={errors.password}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  className={INPUT_CLASS}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={fields.password}
                  onChange={(event) =>
                    setFields((prev) => ({ ...prev, password: event.target.value }))
                  }
                />
              )}
            </Field>

            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}
